import { SerializedStyles, css } from "@emotion/react";
import { Theme, useTheme } from "@mui/material";

import useIsSpMode from "~/lib/client/hooks/useIsSpMode";

const RaidProgressBar = ({
  achievedRate,
  point,
  overrideStyle,
}: {
  achievedRate: number;
  point: string;
  overrideStyle?: SerializedStyles;
}) => {
  const theme = useTheme();
  const isSpMode = useIsSpMode() ?? false;

  return (
    <div css={[rootStyle(theme), overrideStyle]}>
      <div css={achievedRateLinesStyle(isSpMode)}>
        {[...Array(50)].map((_, i) => {
          return (
            <div
              key={`achievedRateLine-${i}`}
              css={achievedRateLineStyle(theme)}
            />
          );
        })}
      </div>

      <div css={innerBarStyle(theme, achievedRate, isSpMode)}>
        <div css={achievedRateStyle(theme)}> {achievedRate}%</div>
        <div css={pointContainerStyle(theme, isSpMode)}>
          <span css={pointStyle}>{point}</span>
        </div>
      </div>
    </div>
  );
};

const rootStyle = (theme: Theme) => css`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 2.5rem;
  border-radius: 10000px;
  background-color: ${theme.palette.background.default};
  padding: 4px;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.25);
`;

const innerBarStyle = (
  theme: Theme,
  achievedRate: number,
  isSpMode: boolean
) => {
  const width = achievedRate >= 100 ? 100 : achievedRate;
  const calculationWidth = isSpMode ? width * 0.95 : width * 0.975;
  const borderRadius = achievedRate >= 100 ? "10000px" : "10000px 0 0 10000px";

  return css`
    position: relative;
    display: flex;
    align-items: center;
    padding-left: 1rem;
    width: ${isSpMode
      ? `${calculationWidth}%`
      : `calc(${calculationWidth}% - 0.5rem)`};
    height: 100%;
    border-radius: ${borderRadius};
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.25);
    background: linear-gradient(
      to left,
      rgba(255, 72, 15, 0.9) 0%,
      rgba(255, 158, 129, 0.5) 100%
    );
  `;
};

const achievedRateStyle = (theme: Theme) => css`
  font-size: 1rem;
  font-weight: bold;
  color: ${theme.palette.primary.contrastText};
`;

const achievedRateLinesStyle = (isSpMode: boolean) => css`
  position: absolute;
  display: flex;
  width: 100%;
  height: 1.5rem;
  padding-inline: ${isSpMode ? "1.5rem" : "2rem"};
  justify-content: space-between;
`;

const achievedRateLineStyle = (theme: Theme) => css`
  height: 100%;
  width: 1px;
  background-color: ${theme.palette.divider};
`;

const pointContainerStyle = (theme: Theme, isSpMode: boolean) => css`
  position: absolute;
  top: 0;
  right: 0;
  color: ${theme.palette.primary.main};
  display: flex;
  align-items: flex-end;
  gap: 4px;
  font-weight: bold;

  &::after {
    content: "";
    display: block;
    width: 1px;
    height: ${isSpMode ? "3.3rem" : "3.5625rem"};
    background-color: ${theme.palette.text.primary};
  }
`;

const pointStyle = css`
  transform: translateY(0.2rem);
`;

export default RaidProgressBar;
